import { compareZ } from './fractionalIndex';
import type { MindmapObject } from './types';

export const MINDMAP_GAP_X = 60;
export const MINDMAP_GAP_Y = 16;

interface Node {
  obj: MindmapObject;
  children: Node[];
  height: number;
}

/**
 * Tidy horizontal tree layout for one mind map. The root keeps its position; first-level
 * children go to the left or right side by `side`, deeper levels inherit the side.
 * Returns new top-left positions for every non-root visible node.
 */
export function layoutMindmap(
  nodes: readonly MindmapObject[],
): Map<string, { x: number; y: number }> {
  const root = nodes.find((n) => n.parentNode === null);
  const out = new Map<string, { x: number; y: number }>();
  if (!root) return out;

  const byParent = new Map<string, MindmapObject[]>();
  for (const n of nodes) {
    if (!n.parentNode) continue;
    const list = byParent.get(n.parentNode) ?? [];
    list.push(n);
    byParent.set(n.parentNode, list);
  }
  for (const list of byParent.values())
    list.sort((a, b) => compareZ({ z: a.order, id: a.id }, { z: b.order, id: b.id }));

  const build = (obj: MindmapObject, filter?: (o: MindmapObject) => boolean): Node => {
    const kids = obj.collapsed ? [] : (byParent.get(obj.id) ?? []).filter(filter ?? (() => true));
    const children = kids.map((k) => build(k));
    const childrenHeight =
      children.reduce((s, c) => s + c.height, 0) + MINDMAP_GAP_Y * Math.max(0, children.length - 1);
    return { obj, children, height: Math.max(obj.h, childrenHeight) };
  };

  const place = (
    node: Node,
    dir: 1 | -1,
    parentRight: number,
    parentLeft: number,
    centerY: number,
  ) => {
    const x = dir === 1 ? parentRight + MINDMAP_GAP_X : parentLeft - MINDMAP_GAP_X - node.obj.w;
    const y = centerY - node.obj.h / 2;
    out.set(node.obj.id, { x, y });
    placeChildren(node.children, dir, x + node.obj.w, x, centerY);
  };

  const placeChildren = (
    children: Node[],
    dir: 1 | -1,
    right: number,
    left: number,
    centerY: number,
  ) => {
    const total =
      children.reduce((s, c) => s + c.height, 0) + MINDMAP_GAP_Y * Math.max(0, children.length - 1);
    let top = centerY - total / 2;
    for (const c of children) {
      place(c, dir, right, left, top + c.height / 2);
      top += c.height + MINDMAP_GAP_Y;
    }
  };

  const cy = root.y + root.h / 2;
  for (const side of ['right', 'left'] as const) {
    const sideRoot = build(root, (o) => o.side === side);
    placeChildren(sideRoot.children, side === 'right' ? 1 : -1, root.x + root.w, root.x, cy);
  }
  return out;
}

/** Hidden nodes: descendants of collapsed nodes. */
export function hiddenMindmapNodes(nodes: readonly MindmapObject[]): Set<string> {
  const byParent = new Map<string, MindmapObject[]>();
  for (const n of nodes)
    if (n.parentNode) byParent.set(n.parentNode, [...(byParent.get(n.parentNode) ?? []), n]);
  const hidden = new Set<string>();
  const hide = (id: string) => {
    for (const c of byParent.get(id) ?? []) {
      hidden.add(c.id);
      hide(c.id);
    }
  };
  for (const n of nodes) if (n.collapsed) hide(n.id);
  return hidden;
}
